import React, { Component } from "react";
import SectionTitle from "../../components/SectionTitle";
import vec1 from "../../images/contact/1.png";
import vec2 from "../../images/contact/2.png";
import { useLanguage } from "../../contexts/LanguageContext";
import { sendRSVPEmail, initEmailJS } from "../../services/emailService";
import { storeRSVPInGoogleSheets } from "../../services/googleSheetsService";

class RSVPInner extends Component {
  componentDidMount() {
    // Initialize EmailJS when component mounts
    initEmailJS();
  }

  state = {
    name: "",
    email: "",
    phone: "",
    areYouComing: "",
    hasFoodSensitivity: "",
    foodSensitivityDetails: "",
    accommodationPlan: "",
    withAttendant: "",
    attendantName: "",
    message: "",
    error: {},
    isSubmitting: false,
    submitMessage: "",
    submitMessageType: "", // 'success' or 'error'
  };

  changeHandler = (e) => {
    const error = this.state.error;
    error[e.target.name] = "";

    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    const nextState = {
      [e.target.name]: value,
      error,
      submitMessage: "", // Clear previous messages
      submitMessageType: "",
    };

    // Reset conditional fields when user is not coming
    if (e.target.name === "areYouComing" && value !== "yes") {
      nextState.hasFoodSensitivity = "";
      nextState.foodSensitivityDetails = "";
      nextState.accommodationPlan = "";
      nextState.withAttendant = "";
      nextState.attendantName = "";
    }

    // Reset food sensitivity details when user selects "No"
    if (e.target.name === "hasFoodSensitivity" && value !== "yes") {
      nextState.foodSensitivityDetails = "";
    }

    // Reset attendant name when user selects "No"
    if (e.target.name === "withAttendant" && value !== "yes") {
      nextState.attendantName = "";
    }

    this.setState(nextState);
  };

  validateForm = () => {
    const {
      name,
      email,
      areYouComing,
      hasFoodSensitivity,
      foodSensitivityDetails,
      accommodationPlan,
      withAttendant,
      attendantName,
    } = this.state;
    const { t } = this.props;
    const error = {};

    if (!name.trim()) {
      error.name = t("rsvp.errors.name");
    }

    if (!email.trim()) {
      error.email = t("rsvp.errors.email");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      error.email =
        t("rsvp.errors.emailInvalid") || "Please enter a valid email address";
    }

    if (!areYouComing) {
      error.areYouComing = t("rsvp.errors.areYouComing");
    }

    if (areYouComing === "yes") {
      if (!hasFoodSensitivity) {
        error.hasFoodSensitivity = t("rsvp.errors.hasFoodSensitivity");
      }

      if (hasFoodSensitivity === "yes" && !foodSensitivityDetails.trim()) {
        error.foodSensitivityDetails = t("rsvp.errors.foodSensitivityDetails");
      }

      if (!accommodationPlan) {
        error.accommodationPlan = t("rsvp.errors.accommodationPlan");
      }
    }

    if (withAttendant === "yes" && !attendantName.trim()) {
      error.attendantName = t("rsvp.errors.attendantName");
    }

    this.setState({ error });
    return Object.keys(error).length === 0;
  };

  subimtHandler = async (e) => {
    e.preventDefault();
    const { t } = this.props;

    // Validate form
    if (!this.validateForm()) {
      return;
    }

    // Set submitting state
    this.setState({
      isSubmitting: true,
      submitMessage: "",
      submitMessageType: "",
    });

    const formData = {
      name: this.state.name,
      email: this.state.email,
      phone: this.state.phone,
      areYouComing: this.state.areYouComing,
      hasFoodSensitivity: this.state.hasFoodSensitivity,
      foodSensitivityDetails: this.state.foodSensitivityDetails,
      accommodationPlan: this.state.accommodationPlan,
      withAttendant: this.state.withAttendant === "yes",
      attendantName: this.state.attendantName,
      message: this.state.message,
    };

    try {
      // Send email first
      const emailResult = await sendRSVPEmail(formData);

      // Store in Google Sheets
      const sheetsResult = await storeRSVPInGoogleSheets(formData);

      // Check results
      if (emailResult.success && sheetsResult.success) {
        // Success - reset form and show success message
        this.setState({
          name: "",
          email: "",
          phone: "",
          areYouComing: "",
          hasFoodSensitivity: "",
          foodSensitivityDetails: "",
          accommodationPlan: "",
          withAttendant: "",
          attendantName: "",
          message: "",
          error: {},
          isSubmitting: false,
          submitMessage: t("rsvp.success"),
          submitMessageType: "success",
        });
      } else if (emailResult.success) {
        // Email sent but sheets failed
        this.setState({
          isSubmitting: false,
          submitMessage:
            t("rsvp.partialSuccess") ||
            "Email sent but failed to save to database. Please contact us directly.",
          submitMessageType: "error",
        });
      } else if (sheetsResult.success) {
        // Sheets saved but email failed
        this.setState({
          isSubmitting: false,
          submitMessage:
            t("rsvp.emailError") ||
            "Failed to send email but response was saved.",
          submitMessageType: "error",
        });
      } else {
        // Both failed
        this.setState({
          isSubmitting: false,
          submitMessage:
            t("rsvp.error") ||
            "Failed to submit. Please try again or contact us directly.",
          submitMessageType: "error",
        });
      }
    } catch (error) {
      console.error("RSVP submission error:", error);
      this.setState({
        isSubmitting: false,
        submitMessage:
          t("rsvp.error") || "An unexpected error occurred. Please try again.",
        submitMessageType: "error",
      });
    }
  };

  render() {
    const {
      name,
      email,
      phone,
      areYouComing,
      hasFoodSensitivity,
      foodSensitivityDetails,
      accommodationPlan,
      withAttendant,
      attendantName,
      message,
      error,
      isSubmitting,
      submitMessage,
      submitMessageType,
    } = this.state;
    const { t } = this.props;

    return (
      <section className="wpo-contact-section section-padding" id="RSVP">
        <div className="container">
          <div className="wpo-contact-section-wrapper">
            <div className="wpo-contact-form-area">
              <SectionTitle
                topTitle={t("rsvp.topTitle")}
                MainTitle={t("rsvp.title")}
              />
              {submitMessage && (
                <div
                  className={`submit-message ${submitMessageType}`}
                  style={{
                    padding: "15px",
                    marginBottom: "20px",
                    borderRadius: "5px",
                    backgroundColor:
                      submitMessageType === "success" ? "#d4edda" : "#f8d7da",
                    color:
                      submitMessageType === "success" ? "#155724" : "#721c24",
                    border: `1px solid ${
                      submitMessageType === "success" ? "#c3e6cb" : "#f5c6cb"
                    }`,
                    textAlign: "center",
                  }}
                >
                  {submitMessage}
                </div>
              )}
              <form onSubmit={this.subimtHandler} className="form">
                <div className="row">
                  <div>
                    <div className="form-field">
                      <input
                        value={name}
                        onChange={this.changeHandler}
                        className="form-control"
                        type="text"
                        name="name"
                        placeholder={t("rsvp.name")}
                      />
                      <p>{error.name ? error.name : ""}</p>
                    </div>
                  </div>
                  <div>
                    <div className="form-field">
                      <input
                        onChange={this.changeHandler}
                        value={email}
                        type="email"
                        className="form-control"
                        name="email"
                        placeholder={t("rsvp.email")}
                      />
                      <p>{error.email ? error.email : ""}</p>
                    </div>
                  </div>
                  <div>
                    <div className="form-field">
                      <input
                        onChange={this.changeHandler}
                        value={phone}
                        type="tel"
                        className="form-control"
                        name="phone"
                        placeholder={t("rsvp.phone")}
                      />
                      <p>{error.phone ? error.phone : ""}</p>
                    </div>
                  </div>
                  <div>
                    <div className="form-field">
                      <select
                        name="areYouComing"
                        className="form-control"
                        value={areYouComing}
                        onChange={this.changeHandler}
                      >
                        <option value="">{t("rsvp.areYouComing")}</option>
                        <option value="yes">{t("rsvp.yes")}</option>
                        <option value="no">{t("rsvp.no")}</option>
                      </select>
                      <p>{error.areYouComing ? error.areYouComing : ""}</p>
                    </div>
                  </div>
                  {areYouComing === "yes" && (
                    <>
                      <div>
                        <div className="form-field">
                          <select
                            name="hasFoodSensitivity"
                            className="form-control"
                            value={hasFoodSensitivity}
                            onChange={this.changeHandler}
                          >
                            <option value="">
                              {t("rsvp.foodSensitivityQuestion")}
                            </option>
                            <option value="yes">{t("rsvp.yes")}</option>
                            <option value="no">{t("rsvp.no")}</option>
                          </select>
                          <p>
                            {error.hasFoodSensitivity
                              ? error.hasFoodSensitivity
                              : ""}
                          </p>
                        </div>
                      </div>
                      {hasFoodSensitivity === "yes" && (
                        <div>
                          <div className="form-field">
                            <input
                              onChange={this.changeHandler}
                              value={foodSensitivityDetails}
                              type="text"
                              className="form-control"
                              name="foodSensitivityDetails"
                              placeholder={t("rsvp.foodSensitivityDetails")}
                            />
                            <p>
                              {error.foodSensitivityDetails
                                ? error.foodSensitivityDetails
                                : ""}
                            </p>
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="form-field">
                          <select
                            name="accommodationPlan"
                            className="form-control"
                            value={accommodationPlan}
                            onChange={this.changeHandler}
                          >
                            <option value="">
                              {t("rsvp.accommodationQuestion")}
                            </option>
                            <option value="travel_home">
                              {t("rsvp.accommodationOptionTravelHome")}
                            </option>
                            <option value="self_arrange">
                              {t("rsvp.accommodationOptionSelfArrange")}
                            </option>
                          </select>
                          <p>
                            {error.accommodationPlan
                              ? error.accommodationPlan
                              : ""}
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="form-field">
                          <select
                            name="withAttendant"
                            className="form-control"
                            value={withAttendant}
                            onChange={this.changeHandler}
                          >
                            <option value="">
                              {t("rsvp.comingWithAttendant")}
                            </option>
                            <option value="yes">{t("rsvp.yes")}</option>
                            <option value="no">{t("rsvp.no")}</option>
                          </select>
                        </div>
                      </div>
                      {withAttendant === "yes" && (
                        <div>
                          <div className="form-field">
                            <input
                              onChange={this.changeHandler}
                              value={attendantName}
                              type="text"
                              className="form-control"
                              name="attendantName"
                              placeholder={t("rsvp.attendantName")}
                            />
                            <p>
                              {error.attendantName ? error.attendantName : ""}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <div className="form-field">
                      <textarea
                        onChange={this.changeHandler}
                        value={message}
                        className="form-control"
                        name="message"
                        placeholder={t("rsvp.message")}
                        rows="4"
                        style={{ minHeight: "100px", resize: "vertical" }}
                      />
                      <p>{error.message ? error.message : ""}</p>
                    </div>
                  </div>
                  <div className="submit-area" style={{ marginTop: "20px" }}>
                    <div className="form-submit">
                      <button
                        type="submit"
                        className="theme-btn-s3"
                        disabled={isSubmitting}
                        style={{
                          opacity: isSubmitting ? 0.6 : 1,
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                        }}
                      >
                        {isSubmitting
                          ? t("rsvp.submitting") || "Submitting..."
                          : t("rsvp.sendMessage")}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
              <div className="border-style"></div>
            </div>
            <div className="vector-1">
              <img src={vec1} alt="" />
            </div>
            <div className="vector-2">
              <img src={vec2} alt="" />
            </div>
          </div>
        </div>
      </section>
    );
  }
}

const RSVP = () => {
  const { t } = useLanguage();
  return <RSVPInner t={t} />;
};

export default RSVP;
